import SkeletonBlock from "@/components/SkeletonBlock";
import IconDropdown from "@/components/ui/IconDropdown";
import { Modal } from "@/components/ui/Modal";
import NotFoundState from "@/components/ui/NotFoundState";
import { cdnCommonPath } from "@/constants/cdnUrls";
import { PATHS } from "@/constants/pathnames";
import { useModal, useModalHelpers } from "@/contexts/ModalContext";
import { useRouter } from "@/i18n/navigation";
import clsx from "clsx";
import Image from "next/image";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import PhoneConfirmationModal from "../account/saved-cards/PhoneConfirmationModal";
import SavedCardDetailModal from "../account/saved-cards/SavedCardDetailModal";
import useSavedCards from "../account/saved-cards/hooks/useSavedCards";

// Constants
const MODAL_IDS = {
  CARD_DETAIL: "saved-card-detail-modal",
  CONFIRMATION: "saved-card-confirmation-modal",
};

const FORM_MODES = {
  ADD: "add",
  EDIT: "edit",
};

const CARD_ACTIONS = {
  UPDATE: "update",
  DELETE: "delete",
  SHOW: "show",
};

const MODAL_CONTENT_CLASS =
  "scrollbar-hide flex-1 overflow-visible p-6 md:px-6 md:pt-4 md:pb-5";

// Sub-components
const SavedCardItem = memo(function SavedCardItem({
  card,
  dropdownItems,
  onOptionClick,
  optionsTitle,
  selectedSavedCard,
  setSelectedSavedCard,
}) {
  const handleItemClick = useCallback(
    (item) => onOptionClick(item, card),
    [card, onOptionClick],
  );

  return (
    <div
      className={clsx(
        "border-border-weak flex cursor-pointer items-center justify-between gap-4 rounded-lg border-2 p-3",
        selectedSavedCard?.Id === card.Id
          ? "border-primary"
          : "border-border-weak",
      )}
      onClick={() => setSelectedSavedCard(card)}
    >
      <div className="flex items-center justify-start gap-4">
        <span
          className={clsx(
            "flex h-6 w-6 items-center justify-center rounded-full transition-all duration-300",
            selectedSavedCard?.Id === card.Id
              ? "bg-primary"
              : "border-primary-weak border bg-white",
          )}
        >
          <span className="h-2 w-2 rounded-full bg-white" />
        </span>
        <figure className="w-10">
          <Image
            src={`${cdnCommonPath}${card.CardOrganizationType}.svg`}
            alt={card.CardOrganizationType}
            width={0}
            height={0}
            sizes="100%"
            className="h-full w-full object-contain"
          />
        </figure>
        <div className="flex flex-1 flex-col items-start gap-1">
          <div className="text-sm">{card.CardName}</div>
          <div className="text-base font-semibold">{card.MaskedCardNumber}</div>
        </div>
      </div>
      <IconDropdown
        items={dropdownItems}
        onItemClick={handleItemClick}
        buttonClassName="w-4 h-4"
        bottomSheetTitle={optionsTitle}
      />
    </div>
  );
});

function SavedCardsContent({ selectedSavedCard, setSelectedSavedCard, t }) {
  // Hooks
  const {
    cardList,
    deleteSavedCard,
    updateSavedCard,
    sendSaveCardOTP,
    saveCard,
    isLoading,
  } = useSavedCards(true);

  const router = useRouter();
  const { confirmModal: deleteConfirmModal, infoModal } = useModalHelpers();
  const savedCardDetailModal = useModal(MODAL_IDS.CARD_DETAIL);
  const confirmModal = useModal(MODAL_IDS.CONFIRMATION);

  const selectFirstCard = useCallback(() => {
    if (cardList.length > 0 && !selectedSavedCard) {
      setSelectedSavedCard(cardList[0]);
    }
  }, [cardList, setSelectedSavedCard]);

  useEffect(() => {
    selectFirstCard();
  }, [cardList]);

  // Consolidated modal state
  const [modalState, setModalState] = useState({
    selectedCard: null,
    title: t("common.addNewCard"),
    formMode: null,
    newCardPayload: null,
  });

  const isEditMode = modalState.formMode === FORM_MODES.EDIT;

  // Memoized dropdown items
  const dropdownItems = useMemo(
    () => [
      { label: t("common.updateName"), action: CARD_ACTIONS.UPDATE },
      { label: t("common.delete"), action: CARD_ACTIONS.DELETE },
      { label: t("common.showAll"), action: CARD_ACTIONS.SHOW },
    ],
    [t],
  );

  // Handlers
  const showSuccessModal = useCallback(
    (title, message) => {
      infoModal({
        variant: "success",
        title,
        message,
        showCancel: false,
      });
    },
    [infoModal],
  );

  const handleAddNewCard = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      selectedCard: null,
      title: t("common.addNewCard"),
      formMode: FORM_MODES.ADD,
    }));
    savedCardDetailModal.open();
  }, [t, savedCardDetailModal]);

  const handleEditCard = useCallback(
    (card) => {
      setModalState((prev) => ({
        ...prev,
        selectedCard: card,
        title: t("common.updateSavedCard"),
        formMode: FORM_MODES.EDIT,
      }));
      savedCardDetailModal.open();
    },
    [t, savedCardDetailModal],
  );

  const handleDeleteCard = useCallback(
    async (card) => {
      const result = await deleteConfirmModal({
        title: t("common.deleteSavedCardTitle"),
        message: t("common.deleteSavedCardDescription", {
          CardMask: card.MaskedCardNumber,
        }),
        showCancel: true,
        variant: "warning",
        confirmButtonText: t("common.removeCard"),
      });

      if (!result.confirmed) return;

      const response = await deleteSavedCard({
        CardId: card.Id,
        CardToken: card.CardToken,
      });

      if (response?.Success) {
        showSuccessModal(
          t("common.cardDeletedResultTitle"),
          t("common.cardDeletedResultDescription"),
        );
      }

      result.close();
    },
    [deleteConfirmModal, deleteSavedCard, showSuccessModal, t],
  );

  const handleShowCard = useCallback(
    (card) => {
      router.push(PATHS.SAVED_CARDS_PAGE.PATH);
    },
    [router],
  );

  const handleOptionClick = useCallback(
    (item, card) => {
      const actions = {
        [CARD_ACTIONS.UPDATE]: () => handleEditCard(card),
        [CARD_ACTIONS.DELETE]: () => handleDeleteCard(card),
        [CARD_ACTIONS.SHOW]: () => handleShowCard(card),
      };

      actions[item.action]?.();
    },
    [handleEditCard, handleDeleteCard, handleShowCard],
  );

  const handleCloseSavedCardDetail = useCallback(() => {
    savedCardDetailModal.close();
    setModalState((prev) => ({
      ...prev,
      selectedCard: null,
      formMode: null,
    }));
  }, [savedCardDetailModal]);

  const handleUpdateCard = useCallback(
    async (payload) => {
      const response = await updateSavedCard(payload);

      if (response?.Success) {
        showSuccessModal(
          t("common.cardUpdatedTitle"),
          t("common.cardUpdatedDescription"),
        );
      }

      savedCardDetailModal.close();
    },
    [updateSavedCard, showSuccessModal, t, savedCardDetailModal],
  );

  const handleSendCode = useCallback(
    async (payload) => {
      const response = await sendSaveCardOTP({
        PhoneNumber: payload.PhoneNumber,
      });

      if (response?.Success) {
        setModalState((prev) => ({ ...prev, newCardPayload: payload }));
        confirmModal.open();
      } else {
        confirmModal.close();
      }

      savedCardDetailModal.close();
    },
    [sendSaveCardOTP, confirmModal, savedCardDetailModal],
  );

  const handleSubmitCard = useCallback(
    async (payload) => {
      if (isEditMode) {
        await handleUpdateCard(payload);
      } else {
        await handleSendCode(payload);
      }
    },
    [isEditMode, handleUpdateCard, handleSendCode],
  );

  const handleConfirmCode = useCallback(
    async (payload) => {
      const response = await saveCard(payload);

      if (response?.Success) {
        confirmModal.close();
        showSuccessModal(
          t("common.cardSavedTitle"),
          t("common.cardSavedDescription"),
        );
      }
    },
    [saveCard, confirmModal, showSuccessModal, t],
  );

  return (
    <div className="flex flex-col gap-6 md:gap-4">
      {/* Header */}
      {/* <div className="flex flex-row items-center justify-between gap-3">
        <h1 className={clsx("text-lg font-semibold", "md:text-[23px]/[30px]")}>
          {t("title")}
        </h1>

        <Button className="!h-9 md:!h-10" onClick={handleAddNewCard}>
          <FontAwesomeIcon
            className="!h-[13px] !w-[13px]"
            icon={byPrefixAndName.far["plus"]}
          />
          {t("addNewCardButton")}
        </Button>
      </div> */}

      {!isLoading ? (
        cardList.length > 0 ? (
          <div className="scrollbar-default grid max-h-[335px] grid-cols-1 gap-2 overflow-y-auto pr-1">
            {cardList.map((card) => (
              <SavedCardItem
                selectedSavedCard={selectedSavedCard}
                setSelectedSavedCard={setSelectedSavedCard}
                key={card.Id}
                card={card}
                dropdownItems={dropdownItems}
                onOptionClick={handleOptionClick}
                optionsTitle={t("common.optionsTitle")}
              />
            ))}
          </div>
        ) : (
          <div>
            <NotFoundState
              title={t("common.notFoundCardsTitle")}
              description={t("common.notFoundCardDescription")}
            />
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 gap-2">
          <SkeletonBlock className="h-[76px] w-full" />
          <SkeletonBlock className="h-[76px] w-full" />
          <SkeletonBlock className="h-[76px] w-full" />
          <SkeletonBlock className="h-[76px] w-full" />
        </div>
      )}

      {/* Card Detail Modal */}
      <Modal
        id={MODAL_IDS.CARD_DETAIL}
        title={modalState.title}
        showCloseButton
        closeOnBackdropClick
        isBottomSheet
        contentClassName={MODAL_CONTENT_CLASS}
      >
        <SavedCardDetailModal
          card={modalState.selectedCard}
          onClose={handleCloseSavedCardDetail}
          onSubmit={handleSubmitCard}
          submitLabel={isEditMode ? t("common.update") : t("common.saveCard")}
        />
      </Modal>

      {/* Phone Confirmation Modal */}
      <Modal
        id={MODAL_IDS.CONFIRMATION}
        title={t("common.smsConfirmationTitle")}
        showCloseButton
        closeOnBackdropClick
        isBottomSheet
        contentClassName={MODAL_CONTENT_CLASS}
      >
        <PhoneConfirmationModal
          payload={modalState.newCardPayload}
          onConfirm={handleConfirmCode}
          onResendCode={handleSendCode}
        />
      </Modal>
    </div>
  );
}

export default SavedCardsContent;
